import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {isPickupShipment} from '@salesforce/retail-react-app/app/utils/shipment-utils'
import {getSubTotal} from '../utils/basket-utils'
import {SHIPPING_COUNTRY_CODES} from '../constants'

function parseConfig(config) {
    let jsonConfig = null
    try {
        if (config) {
            jsonConfig = JSON.parse(config)
        }
    } catch (e) {
        console.log(e.message)
    }
    return jsonConfig
}

const getChangeUpProductID = () => {
    // ChangeUp config
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    var changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        if (jsonConfig) {
            changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
        }
    } catch (e) {
        console.error(e.message)
    }
    return changeUpProductID
}

function getFullAddress(address) {
    const addressLines = [address.address1, address.address2].filter(Boolean)
    const cityLine = [address.city, address.stateCode, address.postalCode]
        .filter(Boolean)
        .join(', ')
    const countryLine =
        SHIPPING_COUNTRY_CODES.find((country) => country.value === address.countryCode)?.label ||
        address.countryCode
    return [...addressLines, cityLine, countryLine].join(', ')
}

export default function useTalkable() {
    const {
        app: {commerceAPI}
    } = getConfig()

    const {talkable} = commerceAPI
    const {config} = talkable || {}
    const jsonConfig = parseConfig(config)
    const {data: customer} = useCurrentCustomer()
    const available = jsonConfig && jsonConfig.enabled
    const init = () => {
        if (!available) return

        if (!window.talkable || !window.talkable.initialized) {
            window._talkableq = window._talkableq || []
            window._talkableq.push([
                'init',
                {
                    site_id: jsonConfig.siteID
                }
            ])
        }
    }

    return {
        getConfig() {
            return jsonConfig
        },
        authenticateCustomer() {
            if (!available) return

            init()

            let data = {}
            if (customer && customer.isRegistered) {
                data = {
                    email: customer.email,
                    first_name: customer.firstName,
                    last_name: customer.lastName,
                    customer_id: customer.customerNo
                }
            }

            window._talkableq.push(['authenticate_customer', data])
        },
        registerPurchase: (order, products) => {
            if (!available) return

            init()

            if (jsonConfig.postCheckoutEnabled) {
                var data = {
                    customer: {
                        email: order?.customerInfo?.email,
                        first_name: order?.billingAddress?.firstName,
                        last_name: order?.billingAddress?.lastName,
                        customer_id: order?.customerInfo?.customerNo
                    },
                    purchase: {
                        order_date: order.creationDate,
                        order_number: order.orderNo,
                        subtotal: getSubTotal(order, getChangeUpProductID()),
                        coupon_code: [],
                        items: []
                    }
                }

                const shipment = order?.shipments?.length
                    ? order.shipments.find((shipment) => {
                          return isPickupShipment(shipment) === false
                      })
                    : null

                if (shipment) {
                    data.purchase.shipping_address = getFullAddress(shipment.shippingAddress)
                    data.purchase.shipping_zip = shipment.shippingAddress.postalCode
                }

                if (order?.couponItems?.length) {
                    data.purchase.coupon_code = order?.couponItems?.map((c) => c.code)
                }

                const productItemsMap = products?.data.reduce(
                    (map, item) => ({...map, [item.id]: item}),
                    {}
                )
                data.purchase.items = order.productItems?.map((pli) => {
                    const productDetail = productItemsMap?.[pli.productId] || {}
                    const variant = {
                        ...pli,
                        ...productDetail,
                        price: pli.price
                    }
                    const imageGroup = findImageGroupBy(variant?.imageGroups, {
                        viewType: 'large',
                        selectedVariationAttributes: variant?.variationValues
                    })
                    const image_url =
                        imageGroup?.images?.[0]?.disBaseLink ||
                        variant?.c_images?.largeImage ||
                        variant?.c_images?.mediumImage ||
                        variant?.c_images?.smallImage ||
                        variant.c_smallImageURL

                    return {
                        image_url: image_url,
                        price: variant.price / variant.quantity,
                        product_id: variant.productId,
                        quantity: variant.quantity,
                        title: variant.productName,
                        url: variant?.slugUrl
                    }
                })

                window._talkableq.push(['register_purchase', data])
            }
        }
    }
}
